import React from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import { Tablecell } from "../../../component/Methods";

const HistoricalSignalsSummary = ({ signalData, isLatestSignal = false, nextSignalDate = null }) => {
  const theme = useTheme();

  if (!signalData || !signalData.periodMetrics) {
    return null;
  }

  const { periodMetrics, date } = signalData;

  function roundit(number) {
    return ((number || 0) * 100).toFixed(2);
  }

  // Helper function to format dates
  const formatDateForRange = (timestamp) => {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day}${getOrdinalSuffix(day)} ${month}`;
  };

  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };



  // Define labels based on whether this is the latest signal or historical
  const getLabels = () => {
    if (isLatestSignal) {
      return {
        week: "Week to Date",
        quarter: "Quarter to Date",
        month: "Month to Date",
        year: "Year to Date",
        lastQuarter: "Last Quarter",
        inception: "Inception to Date"
      };
    } else {
      const nextSignal = new Date(nextSignalDate);

      //Week to date
      const today = new Date();
      const todayDay = today.getDay();
      const daysToLatestMonday = todayDay === 0 ? 6 : todayDay - 1;
      const latestMonday = new Date(today);
      latestMonday.setDate(today.getDate() - daysToLatestMonday);
      const weekEndDate = nextSignal < latestMonday ? formatDateForRange(today.getTime()) : formatDateForRange(nextSignalDate);


      const todayMonth = today.getMonth();
      const todayQuarter = Math.floor(todayMonth / 3);
      const todayQuarterStart = new Date(today.getFullYear(), todayQuarter * 3, 1);
      const quarterEndDate = nextSignal < todayQuarterStart ? formatDateForRange(today.getTime()) : formatDateForRange(nextSignalDate);

      
       //get last quarter start and end date using todays date
       let lastQuarterStart;
       if (todayQuarter === 0) {
         // Q1: last quarter is Q4 of previous year
         lastQuarterStart = new Date(today.getFullYear() - 1, 9, 1); // Oct 1st of previous year
       } else {
         // Q2, Q3, Q4: last quarter is previous quarter of same year
         lastQuarterStart = new Date(today.getFullYear(), (todayQuarter - 1) * 3, 1);
       }
       const lastQuarterEndDate = nextSignal < lastQuarterStart ? formatDateForRange(today.getTime()) : formatDateForRange(nextSignalDate);

      const todayMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEndDate = nextSignal < todayMonthStart ? formatDateForRange(today.getTime()) : formatDateForRange(nextSignalDate);
     

      const todayYear = today.getFullYear();
      const todayYearStart = new Date(todayYear, 0, 1);
      const yearEndDate = nextSignal < todayYearStart ? formatDateForRange(today.getTime()) : formatDateForRange(nextSignalDate);

     
      const inceptionDate = new Date(date);
      const inceptionEndDate = nextSignal < inceptionDate ? formatDateForRange(today.getTime()) : formatDateForRange(nextSignalDate);

      return {
        week: `${formatDateForRange(latestMonday.getTime())} - ${weekEndDate}`,
        quarter: `${formatDateForRange(todayQuarterStart.getTime())} - ${quarterEndDate}`,
        month: `${formatDateForRange(todayMonthStart.getTime())} - ${monthEndDate}`,
        year: `1st Jan - ${yearEndDate}`,
         lastQuarter: `${formatDateForRange(lastQuarterStart.getTime())} - ${lastQuarterEndDate}`,
        inception: `${formatDateForRange(date)} - ${inceptionEndDate}`,
      };
    }
  };

  const labels = getLabels();

  const transformedArray = [
    {
      name: labels.week,
      long: roundit(periodMetrics.WTD?.totalLong || 0),
      short: roundit(periodMetrics.WTD?.totalShort || 0),
      net: roundit(periodMetrics.WTD?.net || 0),
    },

    {
      name: labels.quarter,
      long: roundit(periodMetrics.QTD?.totalLong || 0),
      short: roundit(periodMetrics.QTD?.totalShort || 0),
      net: roundit(periodMetrics.QTD?.net || 0),
    },
    {
      name: labels.month,
      long: roundit(periodMetrics.MTD?.totalLong || 0),
      short: roundit(periodMetrics.MTD?.totalShort || 0),
      net: roundit(periodMetrics.MTD?.net || 0),
    },

    {
      name: labels.year,
      long: roundit(periodMetrics.YTD?.totalLong || 0),
      short: roundit(periodMetrics.YTD?.totalShort || 0),
      net: roundit(periodMetrics.YTD?.net || 0),
    },
    {
      name: labels.lastQuarter,
      long: roundit(periodMetrics.LQ?.totalLong || 0),
      short: roundit(periodMetrics.LQ?.totalShort || 0),
      net: roundit(periodMetrics.LQ?.net || 0),
    },
    {
      name: labels.inception,
      long: roundit(periodMetrics.ITD?.totalLong || 0),
      short: roundit(periodMetrics.ITD?.totalShort || 0),
      net: roundit(periodMetrics.ITD?.net || 0),
    },
  ];

  return (
    <Box sx={{ marginBottom: "16px" }}>
      {/* Title outside the box */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: "16px",
          color: theme.palette.text.primary,
          marginBottom: "8px",
        }}
      >
        Historical Signals Summary
      </Typography>

      {/* Summary Box */}
      <Box
        sx={{
          borderRadius: "8px",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          padding: "12px",
        }}
      >
        <Table
          size="small"
          aria-label="summary table"
          sx={{
            borderCollapse: "collapse",
            width: "100%",
          }}
        >
          <TableBody>
            {/* Header Row */}
            <TableRow
              sx={{
                //   backgroundColor: theme.palette.grey[100] || theme.palette.background.default,

              }}
            >
              <Tablecell
                align="left"
                sx={{
                  fontWeight: "900",
                  fontSize: "12px",
                  paddingLeft: "5px",
                  color: theme.palette.text.primary,
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >

              </Tablecell>
              <Tablecell
                align="right"
                sx={{
                  fontWeight: "500",
                  fontSize: "12px",
                  paddingLeft: "5px",
                  color: theme.palette.text.secondary,
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                LONG
              </Tablecell>
              <Tablecell
                align="right"
                sx={{
                  fontWeight: "500",
                  fontSize: "12px",
                  paddingLeft: "5px",
                  color: theme.palette.text.secondary,
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                SHORT
              </Tablecell>
              <Tablecell
                align="right"
                sx={{
                  fontWeight: "500",
                  fontSize: "12px",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                  color: theme.palette.text.secondary,
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                NET
              </Tablecell>
            </TableRow>

            {/* Data Rows */}
            {transformedArray?.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": {
                    background: theme.palette.grey[200],
                    cursor: "pointer",
                  },
                  backgroundColor: "transparent",
                }}
              >
                <Tablecell
                  align="left"
                  sx={{
                    fontWeight: "900",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: theme.palette.text.primary,
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                  }}
                >
                  {row?.name}
                </Tablecell>

                <Tablecell
                  align="right"
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: row?.long >= 0
                      ? theme.palette.success.main || "#4caf50"
                      : theme.palette.error.main || "#f44336",
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                  }}
                >
                  {row?.long >= 0 ? Math.abs(row?.long)+`%\u00A0` : `(${Math.abs(row?.long)}%)`}
                </Tablecell>

                <Tablecell
                  align="right"
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: row?.short <= 0
                      ? theme.palette.success.main || "#4caf50"
                      : theme.palette.error.main || "#f44336",
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                  }}
                >
                  {row?.short >= 0 ? Math.abs(row?.short)+`%\u00A0` : `(${Math.abs(row?.short)}%)`}
                </Tablecell>

                <Tablecell
                  align="right"
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: row?.net >= 0
                      ? theme.palette.success.main || "#4caf50"
                      : theme.palette.error.main || "#f44336",
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                  }}
                >
                  {row?.net >= 0 ? Math.abs(row?.net)+`%\u00A0` : `(${Math.abs(row?.net)}%)`}
                </Tablecell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default HistoricalSignalsSummary;
